import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, AuthTokens, UserRole, Admin } from '@/types';
import { isTokenExpired, parseJWT } from '@/utils';

interface AuthState {
  user: User | null;
    role: 'admin' | 'user' | null;
  accessToken: string | null;
   admin: Admin | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const createUserFromToken = (token: string): User | null => {
  const payload = parseJWT(token) as
    | {
        sub?: string | number;
        email?: string;
        type?: string;
        role?: string;
        iat?: number;
      }
    | null;

  if (!payload) return null;

  const email = payload.email || 'admin@example.com';
  const roleValue = payload.role || payload.type;
  const role =
    roleValue === UserRole.ADMIN || roleValue === UserRole.MODERATOR ? roleValue : UserRole.USER;
  const timestamp = payload.iat ? new Date(payload.iat * 1000).toISOString() : new Date().toISOString();

  return {
    id: String(payload.sub || email),
    email,
    name: email.split('@')[0] || 'User',
    role,
    createdAt: timestamp,
    updatedAt: timestamp,
    isActive: true,
  };
};

const getStoredUser = (storedUser: string | null, token: string | null): User | null => {
  if (!storedUser) {
    return token ? createUserFromToken(token) : null;
  }

  try {
    return JSON.parse(storedUser) as User;
  } catch (error) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user');
    }
    return token ? createUserFromToken(token) : null;
  }
};

const getInitialState = (): AuthState => {
  if (typeof window === 'undefined') {
    return {
      user: null,
      accessToken: null,
      role: null,
      admin: null,  
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    };
  }

  const storedToken = localStorage.getItem('accessToken');
  const storedRefreshToken = localStorage.getItem('refreshToken');
  const storedUser = localStorage.getItem('user');
   const storedRole = localStorage.getItem('role');
    const storedAdmin = localStorage.getItem('admin');

  // Check if token is expired
  const isTokenValid = storedToken && !isTokenExpired(storedToken);
  const validToken = isTokenValid ? storedToken : null;
  const user = getStoredUser(storedUser, validToken);

  if (!isTokenValid) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

  } else if (!storedUser && user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  return {
    user,
    accessToken: validToken,
    role: isTokenValid ? (storedRole as 'admin' | 'user') : null,
    refreshToken: isTokenValid ? storedRefreshToken || null : null,
        admin: isTokenValid && storedAdmin ? JSON.parse(storedAdmin) : null,
    isAuthenticated: Boolean(validToken),
    isLoading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    login: (state, action: PayloadAction<{ user: User; tokens: AuthTokens }>) => {
      const { user, tokens } = action.payload;
      state.user = user;
      state.accessToken = tokens.accessToken;
      state.refreshToken = tokens.refreshToken || null;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', tokens.accessToken);
        if (tokens.refreshToken) {
          localStorage.setItem('refreshToken', tokens.refreshToken);
        } else {
          localStorage.removeItem('refreshToken');
        }
        localStorage.setItem('user', JSON.stringify(user));
      }
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    },
      setAdminProfile: (state, action: PayloadAction<Admin>) => {
      state.admin = action.payload;

      if (typeof window !== 'undefined') {
        localStorage.setItem('admin', JSON.stringify(action.payload));
      }
    },

    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      
      // Update localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(action.payload));
      }
    },
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken || null;
      state.isAuthenticated = true;

      // Update localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', action.payload.accessToken);
        if (action.payload.refreshToken) {
          localStorage.setItem('refreshToken', action.payload.refreshToken);
        } else {
          localStorage.removeItem('refreshToken');
        }
      }
    },
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    },
  },
});
export const { setLoading, setError, login, logout, setUser, setTokens, clearAuth, setAdminProfile } = authSlice.actions;